import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearAuthState,
  hasStoredAuthFlag,
  persistAuthState,
  readStoredAuthToken,
  readStoredPermission,
  readStoredUserId,
  waitForAuthChanges,
} from "../lib/authTokens";
import { clearStoredAuthCredentials, readStoredAuthCredentials } from "../lib/authCredentials";

type AuthContextValue = {
  token: string | null;
  isAuthenticated: boolean;
  userEmail: string | null;
  userId: string | null;
  permission: number | null;
  isAdmin: boolean;
  login: (token: string, options?: { remember?: boolean; userEmail?: string | null; userId?: string | null; permission?: number | null }) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() => readStoredAuthToken());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => hasStoredAuthFlag());
  const [permission, setPermission] = useState<number | null>(() => readStoredPermission());
  const [userEmail, setUserEmail] = useState<string | null>(() => readStoredAuthCredentials()?.username ?? null);
  const [userId, setUserId] = useState<string | null>(() => readStoredUserId());

  useEffect(() => {
    const unsubscribe = waitForAuthChanges(() => {
      setToken(readStoredAuthToken());
      setIsAuthenticated(hasStoredAuthFlag());
      setPermission(readStoredPermission());
      setUserEmail(readStoredAuthCredentials()?.username ?? null);
      setUserId(readStoredUserId());
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  const login = useCallback((nextToken: string, options?: { remember?: boolean; userEmail?: string | null; userId?: string | null; permission?: number | null }) => {
    const remember = options?.remember ?? true;
    const normalizedPermission =
      typeof options?.permission === "number" && Number.isFinite(options.permission)
        ? Math.max(0, Math.floor(options.permission))
        : null;
    const normalizedUserId = typeof options?.userId === "string" && options.userId.trim().length > 0 ? options.userId.trim() : null;
    persistAuthState(nextToken, remember, normalizedPermission, normalizedUserId);
    setToken(nextToken);
    setIsAuthenticated(true);
    setPermission(normalizedPermission);
    setUserEmail(options?.userEmail ?? readStoredAuthCredentials()?.username ?? null);
    setUserId(normalizedUserId);
  }, []);

  const logout = useCallback(() => {
    clearAuthState();
    clearStoredAuthCredentials();
    setToken(null);
    setIsAuthenticated(false);
    setPermission(null);
    setUserEmail(null);
    setUserId(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      isAuthenticated,
      userEmail,
      userId,
      permission,
      isAdmin: Boolean(permission !== null && permission > 0),
      login,
      logout,
    }),
    [token, isAuthenticated, userEmail, userId, permission, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within <AuthProvider />");
  }
  return context;
}
